import { getSecondaryPriceLabelForLocale } from '@aboutyou/legacy-shared/src/localization/getSecondaryPriceLabelForLocale'
import {
  Currency,
  formatBasePrice,
  formatPrice,
} from '@aboutyou/legacy-shared/src/utils/currency'
import { IProductPriceUnit } from 'services/providers/bapi/entities/products/types'
import { TranslateFnType } from 'store'
import Variant from 'store/entities/Product/Variant'
import { LocaleType } from 'types/locale'

interface PriceRange {
  min: IProductPriceUnit
  max: IProductPriceUnit
}

export const getFormattedEntityPrices = ({
  productPriceRange = null,
  selectedVariant = null,
  productVariants,
  variantPrice = null,
  config,
}: {
  productPriceRange?: PriceRange | null
  selectedVariant: Variant | null
  productVariants: Variant[]
  variantPrice?: IProductPriceUnit | null
  config: {
    currency: Currency
    locale: LocaleType
    t: TranslateFnType
    isSaleCampaign: boolean
    features?: Record<string, boolean>
    currencyCode: string
  }
}) => {
  const { currency, locale, t, isSaleCampaign, currencyCode } = config

  const prices = variantPrice || (productPriceRange && productPriceRange.min)

  if (!prices) {
    return null
  }

  const showFromPrice =
    !variantPrice && productPriceRange
      ? getShowFromPrice(productPriceRange)
      : false

  const formattedPrices = getFormattedPricesFromPriceUnit(prices, {
    currency,
    locale,
    showFromPrice,
    t,
    currencyCode,
  })

  const formattedReductions = getSaleAndCampaignReductions({
    productPrices: prices,
    selectedVariant,
    productVariants,
    t,
  })

  return {
    basePrice: {
      formattedPrice: formattedPrices.basePrice,
      raw: formattedPrices.basePriceRaw,
      secondaryPriceLabel: getSecondaryPriceLabelForLocale({
        amount: prices.basePrice.price,
        locale,
        phase1: config?.features?.enableSecondaryLabelPhase1,
        phase2: config?.features?.enableSecondaryLabelPhase2,
        currency,
      }),
    },
    salePrice: {
      formattedPrice: formattedPrices.salePrice,
      formattedReduction: formattedReductions.saleReduction,
      raw: formattedPrices.salePriceRaw,
      secondaryPriceLabel: getSecondaryPriceLabelForLocale({
        amount: prices.salePrice?.price,
        locale,
        phase1: config?.features?.enableSecondaryLabelPhase1,
        phase2: config?.features?.enableSecondaryLabelPhase2,
        currency,
      }),
    },
    campaignPrice: {
      formattedPrice: isSaleCampaign ? formattedPrices.campaignPrice : null,
      formattedReduction: isSaleCampaign
        ? formattedReductions.campaignReduction
        : null,
      raw: formattedPrices.campaignPriceRaw,
      secondaryPriceLabel: getSecondaryPriceLabelForLocale({
        amount: prices.campaignPrice?.price,
        locale,
        phase1: config?.features?.enableSecondaryLabelPhase1,
        phase2: config?.features?.enableSecondaryLabelPhase2,
        currency,
      }),
    },
  }
}

const getShowFromPrice = (productPriceRange: PriceRange): boolean => {
  let minPrice = productPriceRange.min.basePrice.price
  let maxPrice = productPriceRange.max.basePrice.price

  if (productPriceRange.min.salePrice) {
    minPrice = productPriceRange.min.salePrice.price
  }
  if (productPriceRange.max.salePrice) {
    maxPrice = productPriceRange.max.salePrice.price
  }
  if (productPriceRange.min.campaignPrice) {
    minPrice = productPriceRange.min.campaignPrice.price
  }
  if (productPriceRange.max.campaignPrice) {
    maxPrice = productPriceRange.max.campaignPrice.price
  }

  return minPrice !== maxPrice
}

export const getFormattedPricesFromPriceUnit = (
  priceUnit: IProductPriceUnit,
  config: {
    currency: Currency
    locale: LocaleType
    showFromPrice: boolean
    t: TranslateFnType
    currencyCode: string
  },
) => {
  const { basePrice, salePrice, campaignPrice } = priceUnit
  const { currency, showFromPrice, t, currencyCode } = config
  const isSale = Boolean(salePrice)
  const isCampaign = Boolean(campaignPrice)

  const basePriceString = formatPrice(basePrice.price, currencyCode, currency)
  const salePriceString = salePrice
    ? formatPrice(salePrice.price, currencyCode, currency)
    : null
  const campaignPriceString = campaignPrice
    ? formatPrice(campaignPrice.price, currencyCode, currency)
    : null

  if (!showFromPrice) {
    return {
      basePrice: basePriceString,
      basePriceRaw: basePrice.price,
      salePrice: salePriceString,
      salePriceRaw: isSale ? salePrice!.price : null,
      campaignPrice: campaignPriceString,
      campaignPriceRaw: isCampaign ? campaignPrice!.price : null,
    }
  } else if (!isSale && !isCampaign) {
    return {
      basePrice: t('ab {{price}}', { price: basePriceString }),
      basePriceRaw: basePrice.price,
      salePrice: null,
      campaignPrice: null,
    }
  } else if (isSale && !isCampaign) {
    return {
      basePrice: basePriceString,
      basePriceRaw: basePrice.price,
      salePrice: t('ab {{price}}', { price: salePriceString }),
      salePriceRaw: isSale ? salePrice!.price : null,
      campaignPrice: null,
    }
  } else if (isCampaign && !isSale) {
    return {
      basePrice: basePriceString,
      basePriceRaw: basePrice.price,
      salePrice: null,
      campaignPrice: t('ab {{price}}', { price: campaignPriceString }),
      campaignPriceRaw: isCampaign ? campaignPrice!.price : null,
    }
  }
  return {
    basePrice: basePriceString,
    basePriceRaw: basePrice.price,
    salePrice: salePriceString,
    salePriceRaw: isSale ? salePrice!.price : null,
    campaignPrice: t('ab {{price}}', { price: campaignPriceString }),
    campaignPriceRaw: isCampaign ? campaignPrice!.price : null,
  }
}

const getSaleAndCampaignReductions = ({
  productPrices,
  selectedVariant,
  productVariants,
  t,
}: {
  productPrices: IProductPriceUnit
  selectedVariant: Variant | null
  productVariants: Variant[]
  t: TranslateFnType
}): { saleReduction: string; campaignReduction: string } => {
  const { salePrice, campaignPrice } = productPrices

  const availableSizes = productVariants.filter(variant => variant.quantity > 0)

  const saleReduction = (() => {
    if (!salePrice) {
      return null
    }

    const variantsHaveTheSameReduction = availableSizes.every(
      size => size.priceUnit.salePrice?.reduction === salePrice?.reduction,
    )

    if (variantsHaveTheSameReduction || selectedVariant) {
      return t('-{{reduction}}%', { reduction: salePrice.reduction })
    }

    const highestSaleReduction = availableSizes.reduce(
      (max, variant) =>
        Math.max(variant.priceUnit.salePrice?.reduction ?? 0, max),
      0,
    )

    return t('bis -{{reduction}}%', { reduction: highestSaleReduction })
  })()

  const campaignReduction = (() => {
    if (!campaignPrice) {
      return null
    }

    const variantsHaveTheSameReduction = availableSizes.every(
      size => size.campaignPrice.reduction === campaignPrice?.reduction,
    )

    if (variantsHaveTheSameReduction || selectedVariant) {
      return t('-{{reduction}}% EXTRA', {
        reduction: campaignPrice.reduction,
      })
    }

    const highestCampaignReduction = availableSizes.reduce(
      (max, variant) =>
        Math.max(variant.priceUnit.campaignPrice?.reduction ?? 0, max),
      0,
    )

    return t('bis -{{reduction}}% EXTRA', {
      reduction: highestCampaignReduction,
    })
  })()

  return {
    saleReduction,
    campaignReduction,
  }
}

export const getPriceNote = (
  productPriceRange: {
    min: IProductPriceUnit
    max: IProductPriceUnit
  } | null,
  selectedVariant: Variant | undefined,
  config: {
    currency: Currency
    locale: LocaleType
    t: TranslateFnType
    currencyCode: string
  },
): string | null => {
  if (!productPriceRange) {
    return null
  }

  const { t, currency, currencyCode } = config

  if (!selectedVariant) {
    if (!productPriceRange.min || !productPriceRange.max) {
      return null
    }

    const taxNote = t('inkl. MwSt.')

    if (productPriceRange.max.reference && productPriceRange.min.reference) {
      const leastPrice =
        productPriceRange.min.reference.withTax <
        productPriceRange.max.reference.withTax
          ? productPriceRange.min
          : productPriceRange.max

      const { reference } = leastPrice

      if (reference) {
        const perPrice =
          formatPrice(reference.withTax, currencyCode, currency) +
          `/${reference.size} ${reference.unit}`
        return (
          `${taxNote}\n` +
          t('ab {{price}}', {
            price: perPrice,
          })
        )
      }
    }

    return `${taxNote}\n`
  }

  if (selectedVariant) {
    const taxNote = t('inkl. MwSt.')

    if (selectedVariant.basePrice) {
      return (
        `${taxNote}\n` +
        formatBasePrice(selectedVariant.basePrice, currency, currencyCode)
      )
    }

    return `${taxNote}\n`
  }

  return null
}
